import type {
  ColumnMeta,
  FkEdge,
  RoutineMeta,
  SchemaSnapshot,
  TableMeta,
  UserTypeMeta,
} from '@/core/engines/intellisenseTypes';
import type {
  OracleSchemaColumn,
  OracleSchemaForeignKey,
  OracleSchemaObject,
  OracleSchemaSnapshot,
} from '@/features/connections/services/language-services/oracleSchemaTypes';
import { getCachedOracleSchema } from '@/features/connections/services/language-services/oracleSchemaCache';
import { classifyJsonColumn } from '@/features/sql-resolver/json/classify/classifyJsonColumn';
import { detectOracleIsJsonColumns } from '@/features/sql-resolver/json/classify/checkIsJsonDetection';
import { projectRoutines } from './projectionHelpers';

function projectColumn(c: OracleSchemaColumn, isJsonChecked: boolean): ColumnMeta {
  const classification = classifyJsonColumn(c.dataType, 'oracle', {
    hasIsJsonCheck: isJsonChecked,
  });
  const base: ColumnMeta = {
    name: c.name,
    dataType: c.dataType,
    nullable: c.isNullable,
    isPrimaryKey: c.isPrimaryKey,
    ordinal: c.ordinalPosition,
  };
  return classification.kind === 'not-json'
    ? base
    : { ...base, jsonClassification: classification };
}

/**
 * Project ONE native relation entry into its bus-shaped `TableMeta`. Exported
 * for the tiered snapshot load (SLICES.md § S7 items 1 + 8): phase 2 merges
 * columns / CHECK clauses page by page and re-projects only the relations a
 * page touched, instead of re-running the whole snapshot projection per page.
 */
export function projectOracleObjectEntry(o: OracleSchemaObject, database: string): TableMeta {
  // Mirror SR-RO projectOracleObject: fold detector hits to uppercase because
  // `ALL_TAB_COLUMNS.COLUMN_NAME` is uppercase but `SEARCH_CONDITION` preserves
  // user-written casing.
  const hits = detectOracleIsJsonColumns(o.checkConstraints ?? []);
  const checkSet = new Set<string>();
  for (const colName of hits.keys()) checkSet.add(colName.toUpperCase());
  return {
    name: o.name,
    schema: o.schema,
    database,
    kind:
      o.type === 'materialized_view'
        ? 'materialized-view'
        : o.type === 'view'
          ? 'view'
          : 'table',
    columns: o.columns.map((c) => projectColumn(c, checkSet.has(c.name.toUpperCase()))),
  };
}

const projectObject = projectOracleObjectEntry;

export function projectOracleForeignKey(fk: OracleSchemaForeignKey): FkEdge {
  return {
    name: fk.name,
    fromSchema: fk.fromSchema,
    fromTable: fk.fromTable,
    fromColumns: fk.fromColumns,
    toSchema: fk.toSchema,
    toTable: fk.toTable,
    toColumns: fk.toColumns,
  };
}

/**
 * Pure projection: native Oracle snapshot → bus `SchemaSnapshot`.
 *
 * Oracle's "schema" maps to the bus's `database` slot — Oracle's session
 * schema is the closest equivalent to MSSQL/PG/MySQL's "current database".
 * Foreign keys come from `ALL_CONSTRAINTS` × `ALL_CONS_COLUMNS` (PRD: Slice
 * "Oracle Resolver — Joins + FK") and are forwarded verbatim — the native
 * shape already aggregates composite keys into one edge per constraint.
 */
export function projectOracleSnapshot(native: OracleSchemaSnapshot): SchemaSnapshot {
  const database = native.schema;
  const tables: TableMeta[] = [];
  for (const t of native.tables) tables.push(projectObject(t, database));
  for (const v of native.views) tables.push(projectObject(v, database));

  const routines: RoutineMeta[] = projectRoutines(native.routinesByNameLower, database);
  const foreignKeys: FkEdge[] = (native.foreignKeys ?? []).map(projectOracleForeignKey);

  // Oracle user-defined types are OBJECT / VARRAY / nested-table / collection
  // types (`ALL_TYPES`). They all surface the same way at a data-type slot, so
  // the kind is recorded as 'object' for the projection's purposes.
  const userTypes: UserTypeMeta[] = (native.userTypes ?? []).map((name) => ({
    name,
    kind: 'object',
  }));

  // OBJECT-type attributes (`ALL_TYPE_ATTRS`) — projected verbatim (type name
  // already uppercase) onto a name → `{ name, dataType }[]` map for the
  // resolver's `t.objcol.▮` attribute completion. Strip ordinal (already
  // ordered) and drop empty maps so the field stays undefined when absent.
  const objectTypeAttributes: Record<
    string,
    { readonly name: string; readonly dataType: string }[]
  > = {};
  for (const [typeName, attrs] of Object.entries(native.typeAttributesByTypeName ?? {})) {
    objectTypeAttributes[typeName.toUpperCase()] = attrs.map((a) => ({
      name: a.name,
      dataType: a.dataType,
    }));
  }

  return {
    engineId: 'oracle',
    connectionId: native.connectionId,
    database,
    schemas: native.schemas ?? [],
    tables,
    routines,
    foreignKeys,
    serverVersion: native.serverVersion || undefined,
    ...(userTypes.length > 0 ? { userTypes } : {}),
    ...(Object.keys(objectTypeAttributes).length > 0 ? { objectTypeAttributes } : {}),
    // Property graphs (`ALL_PROPERTY_GRAPHS` + `ALL_PG_*`) forwarded verbatim —
    // the native shape (`OraclePropertyGraph`) is structurally identical to the
    // bus `PropertyGraphMeta`. Omitted when the schema declares none so the
    // field stays undefined for non-graph schemas.
    ...(native.propertyGraphs && native.propertyGraphs.length > 0
      ? { propertyGraphs: native.propertyGraphs }
      : {}),
    builtAt: native.builtAt,
  };
}

export function buildSnapshot(
  connectionId: string,
  database: string,
): SchemaSnapshot | null {
  const native = getCachedOracleSchema(connectionId);
  if (!native || native.schema !== database) return null;
  return projectOracleSnapshot(native);
}
